/**
 * @file ProductBasicSection.tsx
 * @description «Основное» карточки товара (phase08.5; phase19.31 — общие поля
 * кабинета): категория, название, адрес страницы, короткое описание, теги.
 *
 * Категория — общий `Combobox` с деревом (корень — группой, поиск по
 * названию) вместо нативного списка; маркировка «Честный знак» — словами в
 * пункте, а не «⚠ ЧЗ». Теги — пилюли-переключатели общего вида.
 *
 * Р3в (SB131): под адресом сохранённого товара при его смене — галочка
 * «Переадресовать со старого адреса» (`RedirectOldSlugCheck`).
 */

import { FileText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Combobox } from "../../../../components/ui/combobox";
import { FilterToggleChips } from "../../../../components/ui/FilterChips";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { catalogTags, type CatalogCategory, type CatalogTag } from "../../../../lib/api-catalog";
import { categoryTreeOptions } from "../../../../lib/catalog-category-tree";
import { productSlugChanged, type ProductFormState } from "../product-form";
import { SectionCard } from "./SectionCard";
import { RedirectOldSlugCheck } from "../../../../components/site-seo/RedirectOldSlugCheck";

interface Props {
  form: ProductFormState;
  categories: CatalogCategory[];
  onChange: (patch: Partial<ProductFormState>) => void;
  disabled?: boolean;
  /** Адрес сохранённого товара; новый товар — null. */
  savedSlug?: string | null;
}

const HINT = "type-caption text-on-surface-variant";

export function ProductBasicSection({ form, categories, onChange, disabled, savedSlug = null }: Props) {
  const [tags, setTags] = useState<CatalogTag[]>([]);
  const categoryOptions = useMemo(() => categoryTreeOptions(categories), [categories]);

  useEffect(() => {
    catalogTags
      .list()
      .then(setTags)
      .catch(() => setTags([]));
  }, []);

  function toggleTag(id: string) {
    const selected = form.tagIds.includes(id);
    onChange({ tagIds: selected ? form.tagIds.filter((t) => t !== id) : [...form.tagIds, id] });
  }

  return (
    <SectionCard icon={<FileText />} title="Основное">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="p-name">Название товара *</Label>
          <Input id="p-name" value={form.displayName} onChange={(e) => onChange({ displayName: e.target.value })} disabled={disabled} required />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor="p-cat">Категория *</Label>
            <Combobox
              id="p-cat"
              options={categoryOptions}
              value={form.categoryId || null}
              onChange={(categoryId) => onChange({ categoryId: categoryId ?? "" })}
              placeholder="Выберите категорию"
              searchPlaceholder="Найти категорию"
              disabled={disabled}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor="p-slug">Адрес страницы</Label>
            <Input id="p-slug" value={form.slug} onChange={(e) => onChange({ slug: e.target.value.toLowerCase() })} disabled={disabled} />
            <p className={HINT}>Если оставить пустым — составим из названия</p>
            <RedirectOldSlugCheck
              oldPath={`/product/${savedSlug ?? ""}`}
              changed={productSlugChanged(form, savedSlug)}
              checked={form.redirectFromOldSlug}
              onChange={(redirectFromOldSlug) => onChange({ redirectFromOldSlug })}
              disabled={disabled}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="p-short">Короткое описание</Label>
          <Textarea id="p-short" value={form.shortDescription} onChange={(e) => onChange({ shortDescription: e.target.value })} disabled={disabled} rows={2} />
          <p className={HINT}>Показывается в карточке товара в списке на сайте</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Теги</Label>
          {tags.length === 0 ? (
            <p className={HINT}>Тегов пока нет — их создают в разделе «Теги»</p>
          ) : (
            <fieldset disabled={disabled} className="min-w-0">
              <FilterToggleChips
                aria-label="Теги товара"
                items={tags.map((t) => ({ key: t.id, label: t.displayName, pressed: form.tagIds.includes(t.id), onToggle: () => toggleTag(t.id) }))}
              />
            </fieldset>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
